
import React, { useState, useEffect, useRef } from "react";
import { Search, Loader2, X, Plus, Calendar } from "lucide-react";
import { apiService } from "@/api/api";

interface Client {
  client_id: string;
  first_name: string;
  last_name: string;
  date_of_birth?: string;
  primary_diagnosis?: string;
  status?: string;
  gender?: string;
  email?: string;
  phone_number?: string;
  high_risk?: boolean;
  other_diagnoses?: string[];
  other_client_factors?: string;
  session_count?: number;
  [key: string]: any;
}

interface SearchInputProps<T> {
  items: T[];
  onSelect: (item: T | null) => void;
  onSelectId?: (id: string | number) => void;
  renderItem?: (item: T) => React.ReactNode;
  filterFunction?: (item: T, searchTerm: string) => boolean;
  placeholder?: string;
  className?: string;
  isLoading?: boolean;
  value?: string;
  getItemId?: (item: T) => string | number;
  getDisplayValue?: (item: T) => string;
  disabled?: boolean;
  hideClearButton?: boolean;
  onClientCreated?: (client: T) => void;
}

function SearchInput<T extends Client>({
  items,
  onSelect,
  onSelectId,
  renderItem,
  filterFunction,
  placeholder = "Search...",
  className = "",
  isLoading = false,
  value,
  getItemId,
  getDisplayValue,
  disabled = false,
  hideClearButton = false,
  onClientCreated,
}: SearchInputProps<T>) {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedItem, setSelectedItem] = useState<T | null>(null);
  const [displayValue, setDisplayValue] = useState("");
  const [filteredItems, setFilteredItems] = useState<T[]>([]);
  const [isFocused, setIsFocused] = useState(false);

  // Quick create state
  const [isCreatingMode, setIsCreatingMode] = useState(false);
  const [dobValue, setDobValue] = useState("");
  const [diagnosisValue, setDiagnosisValue] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const dobInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const isControlled = value !== undefined;
  const inputValue = isControlled ? value :
    (selectedItem ? displayValue : searchTerm);

  // Parse search term into first and last name (split on space)
  const parseSearchTerm = (term: string): { firstName: string; lastName: string } => {
    const trimmed = term.trim();
    const spaceIndex = trimmed.indexOf(' ');

    if (spaceIndex === -1) {
      // No space - entire term is first name
      return { firstName: trimmed, lastName: "" };
    }

    // Split on first space: everything before = first name, everything after = last name
    const firstName = trimmed.substring(0, spaceIndex);
    const lastName = trimmed.substring(spaceIndex + 1).trim();

    return { firstName, lastName };
  };

  // Calculate age from DOB
  const calculateAge = (dob: string | undefined): string => {
    if (!dob) return "";
    const dobDate = new Date(dob);
    if (isNaN(dobDate.getTime())) return "";
    const today = new Date();
    let age = today.getFullYear() - dobDate.getFullYear();
    const monthDiff = today.getMonth() - dobDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dobDate.getDate())) {
      age--;
    }
    return `${age}y`;
  };

  // Format DOB for display
  const formatDOB = (dob: string | undefined): string => {
    if (!dob) return "";
    const date = new Date(dob);
    if (isNaN(date.getTime())) return "";
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  useEffect(() => {
    const currentSearchTerm = isControlled ? (value || "") : searchTerm;

    if (currentSearchTerm.trim() === "") {
      setFilteredItems([]);
      setIsCreatingMode(false);
      return;
    }

    const itemsArray = Array.isArray(items) ? items : [];

    if (itemsArray.length === 0) {
      setFilteredItems([]);
      return;
    }

    const filter = filterFunction || ((item: any, term: string) => {
      try {
        const fullName = `${item.first_name || ''} ${item.last_name || ''}`.toLowerCase();
        return fullName.includes(term.toLowerCase());
      } catch (error) {
        console.warn("Error filtering item:", item, error);
        return false;
      }
    });

    try {
      const filtered = itemsArray.filter(item => filter(item, currentSearchTerm));
      setFilteredItems(filtered);
      // Reset create mode when search term changes
      if (!isCreatingMode) {
        setDobValue("");
        setCreateError(null);
      }
    } catch (error) {
      console.error("Error filtering items:", error);
      setFilteredItems([]);
    }
  }, [searchTerm, value, items, filterFunction, isControlled]);

  // Focus DOB input when entering create mode
  useEffect(() => {
    if (isCreatingMode && dobInputRef.current) {
      dobInputRef.current.focus();
    }
  }, [isCreatingMode]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsFocused(false);
        setIsCreatingMode(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleItemSelect = (item: T) => {
    setSelectedItem(item);
    onSelect(item);

    if (onSelectId && getItemId) {
      const itemId = getItemId(item);
      onSelectId(itemId);
    }

    const itemDisplayValue = getDisplayValue ? getDisplayValue(item) :
      `${item.first_name || ''} ${item.last_name || ''}`.trim();

    setDisplayValue(itemDisplayValue);
    setSearchTerm("");
    setFilteredItems([]);
    setIsFocused(false);
    setIsCreatingMode(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    setIsCreatingMode(false);

    if (selectedItem) {
      setSelectedItem(null);
      setDisplayValue("");
      onSelect(null);
    }
  };

  const handleClear = () => {
    setSearchTerm("");
    setSelectedItem(null);
    setDisplayValue("");
    setFilteredItems([]);
    setIsCreatingMode(false);
    setDobValue("");
    setDiagnosisValue("");
    setCreateError(null);
    onSelect(null);
  };

  const handleCreateClick = () => {
    setIsCreatingMode(true);
    setCreateError(null);
  };

  const handleCreateClient = async () => {
    const currentSearchTerm = isControlled ? (value || "") : searchTerm;
    const { firstName, lastName } = parseSearchTerm(currentSearchTerm);

    console.log("Creating client - Search term:", currentSearchTerm);
    console.log("Creating client - Parsed name:", { firstName, lastName });

    if (!firstName) {
      setCreateError("Please enter a name");
      return;
    }

    setIsCreating(true);
    setCreateError(null);

    try {
      // Build client data with all standard fields
      const clientData = {
        first_name: firstName,
        last_name: lastName || "",
        date_of_birth: dobValue || "",
        primary_diagnosis: diagnosisValue || "",
        status: "Active",
        gender: "Other",
        email: "",
        phone_number: "",
        high_risk: false,
        other_diagnoses: [],
        other_client_factors: "",
      };

      const newClient = await apiService.createClient(clientData);
      console.log("Created new client:", newClient);

      // Extract client from response
      const responseClient = newClient?.data || newClient;

      // Normalize the created client to have all expected fields
      const normalizedClient: Client = {
        client_id: responseClient.client_id || responseClient.id,
        first_name: responseClient.first_name || firstName,
        last_name: responseClient.last_name || lastName || "",
        date_of_birth: responseClient.date_of_birth || dobValue || "",
        primary_diagnosis: responseClient.primary_diagnosis || diagnosisValue || "",
        status: responseClient.status || "Active",
        gender: responseClient.gender || "Other",
        email: responseClient.email || "",
        phone_number: responseClient.phone_number || "",
        high_risk: responseClient.high_risk || false,
        other_diagnoses: responseClient.other_diagnoses || [],
        other_client_factors: responseClient.other_client_factors || "",
        session_count: responseClient.session_count || 0,
      };

      console.log("Normalized client:", normalizedClient);

      // Notify parent of new client
      if (onClientCreated) {
        onClientCreated(normalizedClient as T);
      }

      // Select the new client
      handleItemSelect(normalizedClient as T);

    } catch (error: any) {
      console.error("Error creating client:", error);
      setCreateError(error?.message || "Failed to create client");
    } finally {
      setIsCreating(false);
    }
  };

  const handleCreateFieldKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCreateClient();
    } else if (e.key === 'Escape') {
      setIsCreatingMode(false);
      setDobValue("");
      setDiagnosisValue("");
    }
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // If no results and Enter pressed, go to create mode
    const currentSearchTerm = isControlled ? (value || "") : searchTerm;
    if (e.key === 'Enter' && currentSearchTerm.trim() && filteredItems.length === 0) {
      e.preventDefault();
      setIsCreatingMode(true);
    }
  };

  const safeItems = Array.isArray(items) ? items : [];
  const currentSearchTerm = isControlled ? (value || "") : searchTerm;
  // Always show create option at the bottom when there's a search term
  const showCreateOption = isFocused && currentSearchTerm.trim() !== "" && !isLoading && !disabled;

  // Default render item with enhanced display
  const defaultRenderItem = (item: T) => (
    <div className="flex justify-between items-start w-full">
      <div className="flex-1 min-w-0">
        <div className="font-medium text-gray-900">
          {item.first_name} {item.last_name}
        </div>
        {item.primary_diagnosis && (
          <div className="text-xs text-gray-500 truncate">
            {item.primary_diagnosis}
          </div>
        )}
      </div>
      {item.date_of_birth && (
        <div className="text-xs text-gray-400 ml-2 flex-shrink-0">
          <span className="font-medium">{calculateAge(item.date_of_birth)}</span>
          <span className="mx-1">•</span>
          <span>{formatDOB(item.date_of_birth)}</span>
        </div>
      )}
    </div>
  );

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="relative">
        {isLoading ? (
          <Loader2 className="absolute left-2 sm:left-0 top-1/2 transform -translate-y-1/2 h-5 w-5 sm:h-6 sm:w-6 text-gray-400 animate-spin" />
        ) : (
          <Search className="absolute left-2 sm:left-0 top-1/2 transform -translate-y-1/2 h-5 w-5 sm:h-6 sm:w-6 text-gray-400" />
        )}
        <input
          placeholder={isLoading ? "Loading clients..." : placeholder}
          className={`w-full text-lg sm:text-3xl font-light text-gray-600 placeholder:text-gray-400 bg-transparent border-0 border-b border-gray-300 focus:border-gray-500 outline-none px-0 py-2 pl-8 sm:pl-8 pr-8 transition-colors mobile-touch-target ${
            disabled
              ? 'opacity-50 cursor-not-allowed'
              : isLoading
                ? 'disabled:opacity-50 disabled:cursor-not-allowed'
                : ''
          }`}
          value={inputValue || ""}
          onChange={handleInputChange}
          onFocus={() => setIsFocused(true)}
          onKeyDown={handleSearchKeyDown}
          disabled={isLoading || disabled}
        />
        {(inputValue || "").length > 0 && !isLoading && !disabled && !selectedItem && !hideClearButton && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 sm:right-0 top-1/2 transform -translate-y-1/2 h-6 w-6 text-gray-400 hover:text-gray-600 transition-colors mobile-touch-target"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Loading state */}
      {isFocused && currentSearchTerm.trim() !== "" && isLoading && (
        <div className="absolute w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-10 p-4">
          <div className="flex items-center justify-center gap-2 text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">Loading clients...</span>
          </div>
        </div>
      )}

      {/* Results dropdown */}
      {isFocused && !isLoading && !disabled && (filteredItems.length > 0 || showCreateOption) && (
        <div className="absolute w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-10 max-h-64 overflow-y-auto">
          {/* Existing clients */}
          {filteredItems.map((item, idx) => (
            <div
              key={getItemId ? getItemId(item) : idx}
              className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
              onMouseDown={() => handleItemSelect(item)}
            >
              {renderItem ? renderItem(item) : defaultRenderItem(item)}
            </div>
          ))}

          {/* Create new client option - always at the bottom */}
          {showCreateOption && (
            <div className={filteredItems.length > 0 ? "border-t border-gray-200" : ""}>
              {!isCreatingMode ? (
                <div
                  className="p-3 hover:bg-blue-50 cursor-pointer flex items-center gap-2 text-blue-600"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={handleCreateClick}
                >
                  <Plus className="h-4 w-4" />
                  <span className="font-medium">Create "{currentSearchTerm.trim()}"</span>
                </div>
              ) : (
                <div className="p-3 bg-blue-50">
                  <div className="flex items-center gap-2 text-blue-700 mb-3">
                    <Plus className="h-4 w-4" />
                    <span className="font-medium">Create "{currentSearchTerm.trim()}"</span>
                  </div>
                  <div className="space-y-2">
                    {/* Date of Birth */}
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      <input
                        ref={dobInputRef}
                        type="date"
                        value={dobValue}
                        onChange={(e) => setDobValue(e.target.value)}
                        onKeyDown={handleCreateFieldKeyDown}
                        placeholder="Date of Birth"
                        className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        disabled={isCreating}
                      />
                    </div>
                    {/* Diagnosis */}
                    <div className="flex items-center gap-2">
                      <span className="w-4 h-4 text-gray-400 flex-shrink-0 text-xs font-medium text-center">Dx</span>
                      <input
                        type="text"
                        value={diagnosisValue}
                        onChange={(e) => setDiagnosisValue(e.target.value)}
                        onKeyDown={handleCreateFieldKeyDown}
                        placeholder="Diagnosis (optional)"
                        className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        disabled={isCreating}
                      />
                    </div>
                    {/* Create button */}
                    <div className="flex justify-end pt-1">
                      <button
                        type="button"
                        onClick={handleCreateClient}
                        disabled={isCreating}
                        className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                      >
                        {isCreating ? (
                          <>
                            <Loader2 className="h-3 w-3 animate-spin" />
                            Creating...
                          </>
                        ) : (
                          "Create & Select"
                        )}
                      </button>
                    </div>
                  </div>
                  {createError && (
                    <p className="text-xs text-red-600 mt-2">{createError}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-2">
                    Press Enter to create or Escape to cancel
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default SearchInput;
